import imageCompression from "browser-image-compression"

/**
 * Shrinks a photo in the browser before it is uploaded, so a 5–12 MB phone
 * capture reaches S3 at roughly 0.4–0.6 MB (PSNR ~41 dB against the original
 * at the same size, i.e. visually indistinguishable on a phone or in the report).
 *
 * Tuned to keep detail rather than chase the smallest file: 2048 px still
 * reads a meter, nameplate or bill clearly, and quality starts at 0.9. The
 * library only steps quality down further if a photo is still over
 * MAX_SIZE_MB after resizing, which is rare at these settings.
 *
 * Anything that is not a PNG/JPEG, is already small, or would not get
 * smaller is returned untouched — as is any file the browser fails to decode,
 * so a compression problem never blocks an upload.
 */
const COMPRESSIBLE_TYPES = ["image/jpeg", "image/png"]
const MIN_BYTES_TO_COMPRESS = 500 * 1024
const MAX_SIZE_MB = 1.5
// A photo is only rejected if it is still over this after compression.
const MAX_COMPRESSED_MB = 5
const MAX_DIMENSION = 2048
const INITIAL_QUALITY = 0.9

// Served from our own origin (copied in by prestart/prebuild) rather than the
// library's default CDN, so field devices on patchy networks still get the worker.
const WORKER_LIB_URL = new URL(`${process.env.PUBLIC_URL}/browser-image-compression.js`, window.location.origin).href

export const COMPRESSED_LIMIT_MESSAGE = `This photo couldn't be compressed under ${MAX_COMPRESSED_MB} MB. Please choose or retake another photo.`

export const isCompressibleImage = (file: File) =>
    COMPRESSIBLE_TYPES.includes(file.type) && file.size >= MIN_BYTES_TO_COMPRESS

/**
 * True for a photo still over MAX_COMPRESSED_MB after compressImage — in
 * practice one the browser could not decode, since a decodable photo comes out
 * near MAX_SIZE_MB at most. PDFs, videos and other files have no size limit.
 */
export const exceedsCompressedLimit = (file: File) =>
    COMPRESSIBLE_TYPES.includes(file.type) && file.size > MAX_COMPRESSED_MB * 1024 * 1024

const compressImage = async (file: File): Promise<File> => {
    if (!isCompressibleImage(file)) return file

    try {
        const compressed = await imageCompression(file, {
            maxSizeMB: MAX_SIZE_MB,
            maxWidthOrHeight: MAX_DIMENSION,
            initialQuality: INITIAL_QUALITY,
            fileType: "image/jpeg",
            // Keeps capture time and GPS on JPEGs, which site photos rely on as evidence.
            preserveExif: true,
            useWebWorker: true,
            libURL: WORKER_LIB_URL,
        })
        if (compressed.size >= file.size) return file

        // The presign request is keyed on the file name, so the extension must match the new type.
        const name = `${file.name.replace(/\.[^.]+$/, "")}.jpg`
        return new File([compressed], name, {type: "image/jpeg", lastModified: file.lastModified})
    } catch {
        return file
    }
}

export default compressImage
